import React, { useId, useState } from 'react';
import { EntityPathOption } from '../types';
import { Input } from './pilot-ui/input';
import { Label } from './pilot-ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './pilot-ui/select';

interface EntityPathFieldProps {
  value: string | null;
  // The options inside the caller's scope (FS-890): often none for a writer
  // scoped to one home, so never the test of a custom path.
  options: EntityPathOption[];
  // The signed-in account's device scopes (the JWT `scopes` claim).
  scopes: readonly string[];
  // Set when the option list could not be read, as distinct from an empty one.
  optionsError?: string | null;
  // Never null (FS-911); an emptied custom path is ''.
  onChange: (path: string) => void;
}

const CUSTOM = '__custom__';

// Mirrors the server-side zod regex in deviceController's deviceSchema.
// Also matches the bare superuser path '/' (zero segments) — relevant for
// user_entities scope grants, not for device entity_path.
export const PATH_REGEX = /^\/(?:[^/]+\/)*$/;

// The longest entity_path the API stores (deviceSchema's max).
export const ENTITY_PATH_MAX_LENGTH = 255;

// FS-911: a device with no entity_path matches no scope, root's included, so
// it would vanish for every account; the API refuses null. Shown under the
// field when the path is empty, and as the reason on a Save it disables.
export const ENTITY_PATH_REQUIRED = 'Choose a path; a device must have one.';

/**
 * FS-890: does one of the caller's scopes cover `path`? The client twin of
 * device-data-api's scopeCovers: only scopes shaped like a path count, and
 * coverage is a literal prefix test, so `/a/` covers `/a/b/` but not `/ab/`,
 * and `%`, `_` and `\` in a scope are plain characters.
 */
export function scopeCoversPath(scopes: readonly unknown[] | null | undefined, path: string): boolean {
  if (!Array.isArray(scopes)) return false;
  return scopes.some((s) => typeof s === 'string' && PATH_REGEX.test(s) && path.startsWith(s));
}

/**
 * Why `path` cannot be a device's new entity path, or null when it can as far
 * as the console can tell. The API also requires a configured option to be an
 * ancestor of it (FS-890); a scoped writer's option list may not show that
 * option, so that one is left to the API's 400.
 */
export function validateEntityPath(path: string, scopes: readonly string[] | null | undefined): string | null {
  if (!path) return ENTITY_PATH_REQUIRED;
  if (path.length > ENTITY_PATH_MAX_LENGTH) {
    return `Path must be at most ${ENTITY_PATH_MAX_LENGTH} characters.`;
  }
  if (!PATH_REGEX.test(path)) {
    return "Path must start and end with '/' (e.g. /residential/pilot/home-a/).";
  }
  if (!scopeCoversPath(scopes, path)) {
    const own = (scopes ?? []).filter((s) => typeof s === 'string' && PATH_REGEX.test(s));
    return own.length > 0
      ? `Path is outside your device scope (${own.join(', ')}).`
      : 'Your account has no device scope to place a device in.';
  }
  return null;
}

/**
 * Entity path picker shared by the DeviceList edit modal and the device
 * Settings tab. Renders the entity_path_options the caller's scope covers as
 * a select, plus a "Custom…" choice that reveals a free-text input for paths
 * under a configured option (e.g. /residential/pilot/home-a/ to scope a
 * user_entities grant to specific devices). A custom path is checked here for
 * its shape, its length and the caller's own scopes; the API checks that a
 * configured option is an ancestor. Both editors disable Save on a problem
 * reported by validateEntityPath. There is no unset choice (FS-911): an
 * emptied custom path is passed on as '' and both editors disable Save until
 * a path is chosen.
 *
 * Mount with key={device.id} so custom mode resets on device switch.
 */
const EntityPathField: React.FC<EntityPathFieldProps> = ({ value, options, scopes, optionsError, onChange }) => {
  const [customMode, setCustomMode] = useState(false);
  const fieldId = useId();
  const helpId = useId();
  const errorId = useId();
  const optionsErrorId = useId();

  const inOptions = (path: string | null) => !!path && options.some((o) => o.path === path);
  const missing = !value;
  const customError = customMode ? validateEntityPath(value ?? '', scopes) : null;
  const selectedOption = options.find((option) => option.path === value);
  const selectedLabel = customMode
    ? 'Custom…'
    : selectedOption
      ? `${value}${selectedOption.label ? ` — ${selectedOption.label}` : ''}`
      : value ? `${value} (current, not in lookup)` : undefined;

  const handleSelect = (selected: string | null) => {
    if (selected === null) return;
    if (selected === CUSTOM) {
      setCustomMode(true);
      // Seed the text input with the current value so the operator extends
      // an existing path instead of typing from scratch.
      onChange(value ?? options[0]?.path ?? '/');
      return;
    }
    setCustomMode(false);
    onChange(selected);
  };

  const handleCustomBlur = () => {
    // Snap back to the plain select when the typed path is a known option.
    if (inOptions(value)) setCustomMode(false);
  };

  return (
    <div className="mb-4">
      <Label htmlFor={fieldId} className="block text-sm font-medium mb-1">
        Entity Path:
        <span className="text-xs text-muted-foreground ml-1">(org scope)</span>
      </Label>
      <p id={helpId} className="mb-2 text-xs text-muted-foreground">
        Choose the responsibility path for this device or enter a path within your device scope.
      </p>
      <Select name="entity_path" value={customMode ? CUSTOM : value || null} onValueChange={handleSelect}>
        <SelectTrigger
          id={fieldId}
          className="w-full font-mono"
          title="Choose or enter the device's responsibility path"
          aria-describedby={[helpId, missing ? errorId : null, optionsError ? optionsErrorId : null].filter(Boolean).join(' ')}
        >
          <SelectValue placeholder="— none —">{selectedLabel}</SelectValue>
        </SelectTrigger>
        <SelectContent>
        {/* Force the device's current value to appear even if the lookup
            table no longer contains it; prevents silent stranding when
            admin removes an option still in use. */}
        {!customMode && value && !inOptions(value) && (
          <SelectItem value={value}>{value} (current, not in lookup)</SelectItem>
        )}
        {options.map((opt) => (
          <SelectItem key={opt.path} value={opt.path}>
            {opt.label ? `${opt.path} — ${opt.label}` : opt.path}
          </SelectItem>
        ))}
        <SelectItem value={CUSTOM}>Custom…</SelectItem>
        </SelectContent>
      </Select>
      {!customMode && missing && (
        <p id={errorId} className="text-xs text-destructive mt-1">{ENTITY_PATH_REQUIRED}</p>
      )}
      {optionsError && (
        <p id={optionsErrorId} className="text-xs text-destructive mt-1" role="alert">{optionsError}</p>
      )}
      {customMode && (
        <div className="mt-2">
          <Label htmlFor={`${fieldId}-custom`}>Custom entity path</Label>
          <Input
            id={`${fieldId}-custom`}
            type="text"
            name="entity_path_custom"
            value={value ?? ''}
            onChange={(e) => onChange(e.target.value)}
            onBlur={handleCustomBlur}
            autoFocus
            spellCheck={false}
            className="w-full font-mono"
            aria-invalid={!!customError}
            aria-describedby={customError ? errorId : helpId}
            placeholder="/residential/pilot/home-a/"
          />
          {customError ? (
            <p id={errorId} className="text-xs text-destructive mt-1">{customError}</p>
          ) : (
            <p className="text-xs text-muted-foreground mt-1">
              Must start and end with <code className="font-mono">/</code>, sit inside your
              device scope and under a configured path, e.g.{' '}
              <code className="font-mono">/residential/pilot/home-a/</code>.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default EntityPathField;
